import React from 'react'
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useAuth } from "hooks";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: "",
  customsStatus: "",
  clearanceDate: "",
  requiredDocuments: [],
  customsOfficerId: "",
  isActive: ""
}

const ComplianceForm = ({
  isBusy = null,
  complianceDetail = null,
  onSubmit = () => { },
}) => {

  const navigate = useNavigate();

  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // react hook form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,

  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  // RTK Query
  const { data: entityTypeList = []} =
      useListListQuery({
        type: ENTITY_LIST_CONSTANTS.CUSTOM_STATUS,
        status: "Active",
      });
  const
    {
      data: accounts,
      isLoading: loadingAccounts,
    }
      = useGetDDLAccountListQuery();

  // RTK Query
  const [
    fetchEntities,
    {
      data: entities,
      isLoading: loadingEntities,
    }]
    = useLazyGetDDLEntityListQuery();
  const [
    fetchShipments,
    { data: shipments, isLoading: loadingShipments }
  ] = useLazyGetDDLShipmentListQuery();

  // methods
  const handleOnComplianceSubmit = (data) => {
    const _compliancePayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      shipmentId: data?.shipmentId?._id,
    }
    onSubmit(_compliancePayload);
  };
  const _mappedAccountTypeList = Array.isArray(entityTypeList)
  ? entityTypeList.map((item) => ({
      label: item?.name,
      id: item?._id,
    }))
  : [];

  React.useEffect(() => {
    if (!complianceDetail) return;
    reset({
      ...complianceDetail,
      clearanceDate: complianceDetail?.clearanceDate
        ? new Date(complianceDetail.clearanceDate).toISOString().slice(0, 16)
        : "",
    })
  }, [complianceDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id });
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id })
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipments, viewEntity])

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "")

                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicleId", "")
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

              <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);

                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading Shipment..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="customsOfficerId"
              control={control}
              rules={{ required: "Customs officer Id is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Customs Officer ID"
                  error={!!errors.customsOfficerId}
                  helperText={errors?.customsOfficerId?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="customsStatus"
              control={control}
              rules={{ required: "Add compliance Status" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Custom Status"
                  options={_mappedAccountTypeList?.map((option) => option.label) ?? []}                 
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="clearanceDate"
              control={control}
              rules={{ required: "clearanceDate is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Clearance Date"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true, }}
                  error={!!errors.communicationTime}
                  helperText={errors?.communicationTime?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="requiredDocuments"
              control={control}
              rules={{ required: "Required Documents is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Documents"
                  error={!!errors.requiredDocuments}
                  helperText={errors?.requiredDocuments?.message}
                />
              )}
            />
          </Grid>
          {complianceDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained"
          onClick={handleSubmit(handleOnComplianceSubmit)}
        >
          {complianceDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  )
}

export default ComplianceForm